/**
 * Who is reading this module, and who they are up against.
 *
 * Every surface that shows a module shows the same answer to that question — the
 * card in a feed, the module's own page, the room that holds it — so the answer is
 * assembled once, here, and the rule itself is pure (`#/lib/moduleAudience`).
 *
 * What is decided here rather than there is *who the reader is*, from the six
 * things the running app knows: the session, the account type onboarding recorded,
 * a seat in the classroom the module is being read in, **the grade on the reader's
 * own profile**, the **modules they have published**, and the role a dev preview was
 * told to pretend to be. None of the six is certain on every deployment, which is
 * exactly why the ladder in the lib ranks them instead of trusting one.
 *
 * The grade is the interesting one. The AppView does not return the account type
 * yet, so on a deployment that has not implemented it the *only* evidence that a
 * reader is a learner of a module's level is the level itself — and the profile
 * carries the year the person picked when they signed up, in their country's own
 * words (`1st year`), while the module carries the record's code (`Level_A1`).
 * Both are handed to the lib, which asks whether they name the same year
 * (`levelAgreesWithGrade`).
 *
 * Two things are *fetched* here rather than decided, and both are the reader's own
 * side of the course rather than the module's:
 *
 *  - **The work they have published** — the subjects and years of their own modules
 *    (`#/lib/profileWork`), which is the only evidence that somebody else's module
 *    is *their kind* of course (`moduleIsTheReadersWork`). One cached request, and
 *    the same one their own profile tab reads.
 *  - **Which of the players they follow.** Follow state is on a profile, not on a
 *    play, so the leading rows are looked up once
 *    (`#/components/modules/useModuleFollowing`) and handed to the lib as a set of
 *    dids — bounded to the ten names the copy can reach, and skipped entirely for a
 *    board nobody has played.
 *
 * ## The preview
 *
 * A dev build can invent the class, the reader's seat, a follow and the run — see
 * `useModulePreviewCase` for the store and `#/lib/moduleFixtures` for the cases.
 * That is *all* it does: the invented plays go through the same aggregator, and the
 * invented reader through the same ladder, so a preview cannot show a state the app
 * cannot reach. Nothing reads the store outside `IS_DEV`.
 */
import {useMemo} from 'react'

import {
  buildModuleAudience,
  MODULE_PRIZE_WINNERS,
  type ModuleAudience,
  type ModuleViewer,
  type ModuleViewerSeat,
  type ModuleViewerWork,
} from '#/lib/moduleAudience'
import {
  modulePreviewWorkOf,
  readModuleCasesPreview,
  readModulePreviewFriend,
  readModulePreviewRole,
  readModulePreviewState,
  readModulePreviewWork,
} from '#/lib/moduleFixtures'
import {profileModuleWork} from '#/lib/profileWork'
import {useAccountType} from '#/state/classrooms/account'
import {useActorModulesQuery} from '#/state/queries/module'
import {useCurrentAccountProfile} from '#/state/queries/useCurrentAccountProfile'
import {useSession} from '#/state/session'
import {IS_DEV} from '#/env'
import {useModuleBoard} from './useModuleBoard'
import {useModuleFollowing} from './useModuleFollowing'
import {useModulePreviewCase} from './useModulePreviewCase'

/**
 * The year the reader said they were in, off their own profile.
 *
 * Onboarding writes it as the *label* the country's curriculum uses (`1st year`),
 * not as a record code, and the AppView does not hand it back yet on every
 * deployment — so it is read through the same custom-field route the account type
 * is (`#/state/classrooms/account`), and an absent answer is a real answer.
 */
function readGradeLevel(profile: unknown): string | null {
  const value = (profile as {gradeLevel?: unknown} | undefined)?.gradeLevel
  return typeof value === 'string' && value.trim() ? value : null
}

export function useModuleAudience({
  moduleUri,
  authorDid,
  level,
  subject,
  /** The reader's seat, when the module is being read inside a classroom. */
  seat = null,
}: {
  moduleUri: string | undefined
  /** The account that made the module. */
  authorDid: string | null | undefined
  /** The module's own level code (`Level_A5`), for the peer filter. */
  level?: string | null
  /**
   * The module's own subject code (`Maths`), for the reader's own comparison.
   *
   * Not the peer filter's business — a subject is not a year — and read by one rule
   * only: whether this course is in the reader's line of work
   * (`moduleIsTheReadersWork`).
   */
  subject?: string | null
  seat?: ModuleViewerSeat | null
}): {
  audience: ModuleAudience
  /** Learners who played the module, for a caller that wants the count alone. */
  board: ReturnType<typeof useModuleBoard>['board']
  loading: boolean
  isPreview: boolean
  /**
   * Whether the card should draw every case of the reel rather than the one that is
   * true — handed to the block that draws it, so the chip on the card and the copy
   * behind it are the same question answered once.
   *
   * What the sheet *prints* does not come through here: each of its rows invents its
   * own board (`moduleCaseFacts` in `#/lib/moduleFixtures`). This says only whether
   * the sheet is on, which is why a card whose own class is empty can still draw a
   * full one.
   */
  allCases: boolean
} {
  const {currentAccount} = useSession()
  const accountType = useAccountType()
  const profile = useCurrentAccountProfile()
  const grade = useMemo(() => readGradeLevel(profile), [profile])

  /*
   * The preview, read from its store rather than from a prop.
   *
   * One reader, so the switcher at the top of a post's header, the card in a feed
   * and the module's own page cannot disagree — and so that an arrow press redraws
   * every one of them rather than only the screen it was pressed on.
   *
   * Subscribed through the hook, but read as the *facts* it stores rather than as
   * the case it happens to be on. A case writes all of them, and so does each
   * one-fact switch on its own — the bar on the leaderboard, the param in a
   * `?preview=` link — and a board that only obeyed the case would quietly ignore
   * every one of those.
   */
  useModulePreviewCase()
  const allCases = IS_DEV ? readModuleCasesPreview() : false
  const preview = IS_DEV ? readModulePreviewState() : null
  const forcing = IS_DEV ? readModulePreviewRole() : 'off'
  const forcingWork = IS_DEV ? readModulePreviewWork() : false

  const {board, loading, isPreview} = useModuleBoard({
    moduleUri,
    preview,
  })

  /*
   * The reader's own side of the course: what they teach, off the work they have
   * published.
   *
   * Everything else on the card is about the module or about the class, and this is
   * the one thing the reader *brings* to it — the subjects and the years of their
   * own modules (`moduleIsTheReadersWork`), which is what makes somebody else's
   * module the same kind of course as theirs. Read through the same query the
   * reader's own profile tab uses, so it is one request per session and cached;
   * filtered through the app's own reading of a profile's work
   * (`#/lib/profileWork`), so a module still being drafted, or one marked for a
   * classroom, is not evidence of a line of work; and kept to what is *published*,
   * because the claim is about what this reader has put out and not about what is on
   * their desk. No published work at all is the same answer as an unknown one —
   * nothing here matches — so both are simply no work.
   */
  const {data: myModules} = useActorModulesQuery(currentAccount?.did)
  const work = useMemo<ModuleViewerWork | null>(() => {
    /*
     * The preview's answer first, and it is a different kind of answer: not what
     * this account has published — a preview browser is signed into the maker's own
     * account, and those modules are the ones being looked at — but *the course in
     * front of the reader is their own line*, which is the one form of the fact a
     * device can be told to show (`modulePreviewWorkOf`).
     */
    if (forcingWork) return modulePreviewWorkOf({level, subject})
    const published = profileModuleWork(myModules, {isMe: true}).filter(
      item => item.status === 'published',
    )
    if (published.length === 0) return null
    return {
      levels: published.flatMap(item => (item.level ? [item.level] : [])),
      subjects: published.flatMap(item => (item.subject ? [item.subject] : [])),
    }
  }, [forcingWork, level, myModules, subject])

  /*
   * The reader the rest of this file is about.
   *
   * A forced role outranks being the author, and that is the point of it: the maker
   * is the one reader who can never see the other half of the feature — a preview
   * browser is signed into the maker's own account, because that is whose module
   * there is to look at — so *A student* means a student here and nowhere else.
   */ const as = forcing === 'off' ? null : forcing
  const viewer: ModuleViewer = useMemo(
    () => ({
      did: currentAccount?.did,
      isAuthor:
        !as && !!currentAccount?.did && currentAccount.did === authorDid,
      accountType,
      seat,
      level: grade,
      work,
      as,
    }),
    [accountType, as, authorDid, currentAccount?.did, grade, seat, work],
  )

  /*
   * Who the sentence is allowed to name, and whether the reader follows them.
   *
   * The rows are the leading ten — the widest the copy ever reaches
   * (`MODULE_PRIZE_WINNERS`) — and nothing is looked up for a board nobody has
   * played: a card in a timeline must not pay a request for a class of zero.
   */
  const leadingDids = useMemo(
    () => board.all.slice(0, MODULE_PRIZE_WINNERS).map(row => row.did),
    [board],
  )
  const following = useModuleFollowing(leadingDids)

  /*
   * The fixture friend, in a dev build only.
   *
   * Merged rather than substituted, so the switch cannot hide a real follow. It is
   * a stored fact like the class itself: the option that owns the switch writes it,
   * and a case writes it along with everything else it sets
   * (`#/lib/moduleFixtures`).
   */
  const fixtureFriend = IS_DEV ? readModulePreviewFriend() : false
  const withFriend = useMemo(() => {
    const lead = board.all[0]
    if (!fixtureFriend || !lead) return following
    const next = new Set(following)
    next.add(lead.did)
    return next
  }, [board, fixtureFriend, following])

  return {
    audience: useMemo(
      () =>
        buildModuleAudience({
          board,
          viewer,
          level,
          subject,
          following: withFriend,
        }),
      [board, level, subject, viewer, withFriend],
    ),
    allCases,
    board,
    loading,
    isPreview,
  }
}
